import { AutomationRunner, EncampyCommand, TaskRecorder, TaskResult } from "encampy";

/**
 * Run a multi-phase automation where results from earlier phases
 * can inform subsequent phases.
 *
 * @param url The initial URL to navigate to
 * @param automation The multi-phase automation function
 */
export async function runMultiPhaseAutomation(
  url: string,
  automation: (runner: AutomationRunner) => Promise<void>
): Promise<void> {
  if (!url || url.trim() === "") {
    throw new Error("URL is required");
  }

  // Validate URL format
  try {
    new URL(url);
  } catch {
    throw new Error("Invalid URL format");
  }

  const { encampyConnection } = await import("./connectionManager");

  // Track the tab ID across phases so we execute on the same tab
  let tabId: number | undefined;

  // Create the execute function that sends tasks to the extension
  const execute = async (recorder: TaskRecorder): Promise<TaskResult[]> => {
    const tasks = recorder.getTasks();

    if (tasks.length === 0) {
      return [];
    }

    console.log("Executing phase with tasks:", tasks);

    const response = await encampyConnection.sendMessage({
      command: EncampyCommand.RUN_AUTOMATION_FLOW,
      payload: {
        tasks,
        url: tabId ? undefined : url, // Only set URL on first phase
        tabId, // Reuse same tab for subsequent phases
      },
    });

    console.log("Phase response:", response);

    if (!response.success) {
      throw new Error(response.error || "Automation phase failed");
    }

    // Store tab ID for subsequent phases
    if (response.data?.tabId) {
      tabId = response.data.tabId;
    }

    return response.data?.results || [];
  };

  const runner = new AutomationRunner(execute);

  // Run the multi-phase automation
  await automation(runner);
}
